import { log, assert } from '../app/logger.js';
import { services } from '../app/services.js';
import { Coords } from './coords.js';

// depends on window.Marker

/**
 * Map's info window. Shows a reverse geocode when the map is clicked.
 */
class InfoWindow {
  constructor(map) {
    this.map = map;
    this.tmpMarker = new window.Marker({
      map,
      name: 'infowindow-marker',
      visible: false,
    });

    this.onnewloc = null;
    this.open = false;
    this.timeoutId = null;
    this.popup = null;

    let popupContent = `
      <div class='map-infowindow-container'>
        <div class='map-infowindow-address'>
        <div class='map-infowindow-address-locality'></div>
        <div class='map-infowindow-address-area'></div>
        </div>
        <div class='map-infowindow-coords'></div>
        <div class='map-infowindow-link-container'>
          <span class='link plan-trip-link' tabindex='0'
                title='Schedule a new trip'>I'm going there</span>
          <span class='link add-loc-link' tabindex='0'
                title='Make this location permanent'>Set a new location</span>
        </div>
      </div>
      <div class='sqbutton close-button cls-btn-selector' title='Close'></div>
    `;

    // Info window.
    this.popup = new window.MapPopup(popupContent);

    // A bottom panel, the mobile version.
    this.panelEl = document.createElement('div');
    this.panelEl.classList.add('map-infowindow-panel');
    this.panelEl.innerHTML = `
      <div class='map-infowindow-header cls-btn-selector'>
        <div class='locview-title'>
          <div class='map-infowindow-address'>
          <div class='map-infowindow-address-locality'></div>
          <div class='map-infowindow-address-area'></div>
          </div>
          <div class='map-infowindow-coords'></div>
        </div>
        <img class='map-infowindow-collapse-button'
           src='/images/map-arrow-up.png' title='Close'></div>
      </div>
      <div class='map-panel-footer'>
        <span class='cute-button plan-trip-link' tabindex='0'
              title='Schedule a new trip'>I'm going there</span>
        <span class='cute-blue-button add-loc-link' tabindex='0'
              title='Make this location permanent'>Set a new location</span>
      </div>
    `;

    // Event listeners.
    [this.popup.contentEl, this.panelEl].forEach(el => {
      el.querySelector('.plan-trip-link').addEventListener(
        'click',
        () => {
          this.close();
          window.map_locationAccepted({
            name: this.loc.name,
            type: 'toponym',
            lat: this.loc.lat,
            lng: this.loc.lng,
          });
        },
        false
      );
      el.querySelector('.add-loc-link').addEventListener(
        'click',
        () => {
          this.close();
          this.onnewloc(this.loc);
        },
        false
      );
      el.querySelector('.cls-btn-selector').addEventListener(
        'click',
        () => {
          this.close();
        },
        false
      );
    });
  }

  show(loc, caller) {
    assert(loc, `InfoWindow.show: no loc`);
    this.close();
    if (!this.timeoutId) {
      this.caller = caller;
      this.timeoutId = window.setTimeout(this.doShow.bind(this, loc), 300);
    }
  }

  cancelShow(caller) {
    // Cancel show iif a show was requested by same non empty caller. This is
    // used to prevent the show cancelations interferring initiated by map
    // interactions (map-interaction caller) and location searches (default
    // empty caller).
    if (this.timeoutId && (!caller || this.caller == caller)) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = 0;
    }
  }

  close() {
    this.cancelShow();
    if (this.open) {
      this.panelContainerEl.removeChild(this.panelEl);
      this.popup.deattach();
      this.tmpMarker.deattach();
      this.open = false;
    }
  }

  isShown() {
    return this.open;
  }

  closeIfOpen() {
    if (this.open) {
      this.close();
      return true;
    }
    return false;
  }

  async doShow(loc) {
    await loc.loadIfNeeded();

    log(
      `[map:info-window] show InfoWindow for: (${loc.lat}, ${loc.lng}) coordinates`
    );

    assert(!this.open, 'InfoWindow is shown already');

    let address = loc.name
      ? null
      : await services.location.reverseGeocode(loc.data);
    if (!this.timeoutId) {
      return; // show was canceled
    }

    this.loc = loc;

    this.loc.data.name = this.loc.name || (address && address.locality) || '';
    assert(
      this.loc.name,
      `Reverse geocoding failed for (${loc.lat}, ${loc.lng}) coordinates`
    );

    let title = this.loc.name;
    let subtitle = (address && `${address.area}, ${address.country}`) || '';
    let coords = `${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}`;

    // Panel
    this.panelEl.querySelector('.map-infowindow-address-locality').textContent =
      title;
    this.panelEl.querySelector('.map-infowindow-address-area').textContent =
      subtitle;
    this.panelEl.querySelector('.map-infowindow-coords').textContent = coords;
    this.panelContainerEl.appendChild(this.panelEl);

    // Marker
    this.tmpMarker.position = loc;
    this.tmpMarker.visible = true;
    this.tmpMarker.attachTo(this.map);

    // Popup
    this.popup.contentEl.querySelector(
      '.map-infowindow-address-locality'
    ).textContent = title;
    this.popup.contentEl.querySelector(
      '.map-infowindow-address-area'
    ).textContent = subtitle;
    this.popup.contentEl.querySelector('.map-infowindow-coords').textContent =
      coords;

    this.popup.marker = this.tmpMarker;
    this.popup.position = loc;
    this.popup.attachTo(this.map);

    this.open = true;
    this.timeoutId = 0;

    await this.moveMapIfNeeded(loc);
  }

  get locInfo() {
    return (this.open && this.loc) || null;
  }

  /**
   * Move map to show the location.
   */
  async moveMapIfNeeded(loc) {
    // If the panel overlaps with the location, then center the map at
    // the location.
    let [loc_x, loc_y] = await Coords.toClient(this.map, loc.lat, loc.lng);

    let mapRect = window.map_bounds();
    let panelRect = this.panelEl.getBoundingClientRect();

    // Panel rect may be at left or at bottom of the map.
    let left = panelRect.right < mapRect.right ? panelRect.right : mapRect.left;
    let right = mapRect.right;
    let bottom = panelRect.top > mapRect.top ? panelRect.top : mapRect.bottom;
    let top = mapRect.top;

    let lat = parseFloat(loc.lat);
    let lng = parseFloat(loc.lng);

    // 300 ms is not only the animation effect. It also makes the maps to center
    // correctly.
    if (loc_x < left || loc_x > right || loc_y < top || loc_y > bottom) {
      window.setTimeout(() => this.map.panTo({ lat, lng }), 300);
    }
  }

  get panelContainerEl() {
    return window.document.querySelector('maps-panel');
  }
}

export { InfoWindow };
